import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, staffAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Plus, Eye, FolderOpen, X, Send, Pencil, Search, Filter, Trash2, MoreVertical, RefreshCw } from 'lucide-react';
import { BulkSelectCheckbox, BulkActionBar } from '@/components/BulkActions';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';

const COMMON_MAKES = [
  'Volvo', 'Kenworth', 'Peterbilt', 'Freightliner', 'International',
  'Mack', 'Western Star', 'Hino', 'Isuzu', 'Ford', 'Chevrolet',
  'Great Dane', 'Utility', 'Hyundai', 'Stoughton', 'Wabash', 'Vanguard',
];

export default function AdminClients() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', company: '', address: '', city: '', province: '', postal_code: '', notes: '', preferred_makes: [], salesperson: '', salesperson_id: '' });
  const [makeInput, setMakeInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [staffList, setStaffList] = useState([]);

  // Search and filters
  const [searchQuery, setSearchQuery] = useState('');
  const [portalFilter, setPortalFilter] = useState('all');
  const [selected, setSelected] = useState([]);
  const toggleSelect = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);

  const loadClients = () => {
    setLoading(true);
    clientsAPI.list().then(r => { setClients(r.data); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { loadClients(); }, []);
  useEffect(() => { if (isAdmin) staffAPI.listForDropdown().then(r => setStaffList(r.data || [])).catch(() => {}); }, [isAdmin]);

  // Filtered clients
  const filteredClients = useMemo(() => {
    return clients.filter(c => {
      if (searchQuery) {
        const query = searchQuery.toLowerCase();
        const matchesSearch = 
          c.name?.toLowerCase().includes(query) ||
          c.email?.toLowerCase().includes(query) ||
          c.phone?.toLowerCase().includes(query) ||
          c.company?.toLowerCase().includes(query) ||
          c.salesperson?.toLowerCase().includes(query);
        if (!matchesSearch) return false;
      }
      if (portalFilter !== 'all') {
        if (portalFilter === 'active' && !c.portal_registered) return false;
        if (portalFilter === 'invited' && (!c.invite_sent || c.portal_registered)) return false;
        if (portalFilter === 'none' && (c.invite_sent || c.portal_registered)) return false;
      }
      return true;
    });
  }, [clients, searchQuery, portalFilter]);

  const clearFilters = () => {
    setSearchQuery('');
    setPortalFilter('all');
  };

  const hasActiveFilters = searchQuery || portalFilter !== 'all';

  const [duplicateClient, setDuplicateClient] = useState(null);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setDuplicateClient(null);
    try {
      const payload = { ...form };
      // If staff creating client, auto-assign to themselves
      if (!isAdmin && !editId) {
        payload.salesperson = user?.name || '';
        payload.salesperson_id = user?.id || '';
      }
      if (editId) {
        await clientsAPI.update(editId, payload);
        toast.success('Client updated');
      } else {
        await clientsAPI.create(payload);
        toast.success('Client added');
      }
      setDialogOpen(false);
      setEditId(null);
      setForm({ name: '', email: '', phone: '', company: '', address: '', city: '', province: '', postal_code: '', notes: '', preferred_makes: [], salesperson: '', salesperson_id: '' });
      setMakeInput('');
      loadClients();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (err.response?.status === 409 && detail?.existing_client) {
        setDuplicateClient(detail.existing_client);
        toast.error(detail.message);
      } else {
        toast.error(typeof detail === 'string' ? detail : 'Failed');
      }
    }
    setSaving(false);
  };

  const openEdit = (c) => {
    setForm({
      name: c.name || '', email: c.email || '', phone: c.phone || '',
      company: c.company || '', address: c.address || '', city: c.city || '',
      province: c.province || '', postal_code: c.postal_code || '',
      notes: c.notes || '',
      preferred_makes: c.preferred_makes || [], salesperson: c.salesperson || '',
      salesperson_id: c.salesperson_id || '',
    });
    setEditId(c.id);
    setMakeInput('');
    setDialogOpen(true);
  };

  const openNew = () => {
    setForm({ name: '', email: '', phone: '', company: '', address: '', city: '', province: '', postal_code: '', notes: '', preferred_makes: [], salesperson: isAdmin ? '' : (user?.name || ''), salesperson_id: isAdmin ? '' : (user?.id || '') });
    setEditId(null);
    setMakeInput('');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!isAdmin) return; // Only admin can delete
    if (!window.confirm('Delete this client? This cannot be undone.')) return;
    try {
      await clientsAPI.delete(id);
      toast.success('Client deleted');
      loadClients();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to delete'); }
  };

  return (
    <div data-testid="admin-clients">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900 uppercase">
          {isAdmin ? 'Clients' : 'My Clients'}
        </h1>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-500">{filteredClients.length} of {clients.length} clients</span>
          <Button className="bg-[#E31937] text-white hover:bg-[#C4122C]" onClick={openNew} data-testid="add-client-btn">
            <Plus className="w-4 h-4 mr-1" /> Add Client
          </Button>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg border border-gray-200 p-4 mb-4">
        <div className="flex flex-wrap gap-3 items-center">
          <div className="relative flex-1 min-w-[250px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input placeholder="Search by name, email, phone, company..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 h-9 text-sm" data-testid="search-input" />
          </div>
          <Select value={portalFilter} onValueChange={setPortalFilter}>
            <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="portal-filter">
              <Filter className="w-3 h-3 mr-1.5 text-gray-400" /><SelectValue placeholder="Portal" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Portal</SelectItem>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="invited">Invited</SelectItem>
              <SelectItem value="none">Not Invited</SelectItem>
            </SelectContent>
          </Select>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="h-9 text-xs text-gray-500">
              <X className="w-3 h-3 mr-1" /> Clear
            </Button>
          )}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="border-gray-200 hover:bg-transparent">
              <TableHead className="w-10"><BulkSelectCheckbox checked={selected.length === filteredClients.length && filteredClients.length > 0} onCheckedChange={() => setSelected(p => p.length === filteredClients.length ? [] : filteredClients.map(c => c.id))} testId="select-all-clients" /></TableHead>
              <TableHead className="text-gray-500">Name</TableHead>
              <TableHead className="text-gray-500 hidden md:table-cell">Company</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Email</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Phone</TableHead>
              <TableHead className="text-gray-500 hidden lg:table-cell">Pref. Makes</TableHead>
              {isAdmin && <TableHead className="text-gray-500 hidden xl:table-cell">Salesperson</TableHead>}
              <TableHead className="text-gray-500 hidden md:table-cell">Portal</TableHead>
              <TableHead className="text-gray-500 hidden sm:table-cell">Created</TableHead>
              <TableHead className="text-gray-500 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center text-gray-400 py-8">Loading...</TableCell></TableRow>
            ) : filteredClients.length === 0 ? (
              <TableRow><TableCell colSpan={isAdmin ? 10 : 9} className="text-center text-gray-400 py-8">{hasActiveFilters ? 'No clients match your filters' : 'No clients yet'}</TableCell></TableRow>
            ) : filteredClients.map(c => (
              <TableRow key={c.id} className="border-gray-200 text-gray-700">
                <TableCell><BulkSelectCheckbox checked={selected.includes(c.id)} onCheckedChange={() => toggleSelect(c.id)} testId={`select-client-${c.id}`} /></TableCell>
                <TableCell className="font-medium">{c.name}</TableCell>
                <TableCell className="text-sm hidden md:table-cell">{c.company || '--'}</TableCell>
                <TableCell className="text-xs hidden sm:table-cell">{c.email}</TableCell>
                <TableCell className="text-xs hidden lg:table-cell">{c.phone || '--'}</TableCell>
                <TableCell className="hidden lg:table-cell">
                  <div className="flex flex-wrap gap-0.5">
                    {(c.preferred_makes || []).length > 0
                      ? c.preferred_makes.map(m => <Badge key={m} variant="outline" className="text-[10px] border-gray-300 text-gray-700">{m}</Badge>)
                      : <span className="text-xs text-gray-400">--</span>
                    }
                  </div>
                </TableCell>
                {isAdmin && <TableCell className="text-xs hidden xl:table-cell">{c.salesperson || '--'}</TableCell>}
                <TableCell className="hidden md:table-cell">
                  {c.portal_registered ? (
                    <Badge className="bg-green-100 text-green-800 border border-green-300 text-[10px]">Active</Badge>
                  ) : c.invite_sent ? (
                    <Button variant="ghost" size="sm" className="text-amber-600 text-[10px] h-6 px-2 hover:bg-amber-50" onClick={async () => {
                      try {
                        const res = await clientsAPI.sendInvite(c.id);
                        toast.success(res.data?.message || `Invite re-sent to ${c.email}`);
                      } catch (err) {
                        toast.error(err.response?.data?.detail || 'Failed to resend invite');
                      }
                      loadClients();
                    }} data-testid={`resend-invite-${c.id}`}>
                      <RefreshCw className="w-3 h-3 mr-1" /> Resend
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="text-blue-500 text-[10px] h-6 px-2" onClick={async () => {
                      try { 
                        const res = await clientsAPI.sendInvite(c.id); 
                        toast.success(res.data?.message || `Invite sent to ${c.email}`); 
                      } catch (err) { 
                        toast.error(err.response?.data?.detail || 'Failed to send invite'); 
                      }
                      loadClients();
                    }} data-testid={`invite-${c.id}`}>
                      <Send className="w-3 h-3 mr-1" /> Invite
                    </Button>
                  )}
                </TableCell>
                <TableCell className="text-xs text-gray-400 hidden sm:table-cell">{c.created_at ? new Date(c.created_at).toLocaleDateString() : '--'}</TableCell>
                <TableCell className="text-right">
                  {/* Desktop actions */}
                  <div className="hidden sm:flex items-center justify-end gap-1">
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setViewClient(c)} data-testid={`view-${c.id}`}>
                      <Eye className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => navigate(`/admin/clients/${c.id}`)} data-testid={`portfolio-${c.id}`}>
                      <FolderOpen className="w-3 h-3" />
                    </Button>
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(c)} data-testid={`edit-${c.id}`}>
                      <Pencil className="w-3 h-3" />
                    </Button>
                    {isAdmin && (
                      <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-red-400" onClick={() => handleDelete(c.id)} data-testid={`delete-${c.id}`}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {/* Mobile dropdown */}
                  <div className="sm:hidden">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" data-testid={`actions-menu-client-${c.id}`}><MoreVertical className="w-4 h-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-white">
                        <DropdownMenuItem onClick={() => setViewClient(c)}><Eye className="w-3.5 h-3.5 mr-2" />View</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/admin/clients/${c.id}`)}><FolderOpen className="w-3.5 h-3.5 mr-2" />Portfolio</DropdownMenuItem>
                        <DropdownMenuItem onClick={() => openEdit(c)}><Pencil className="w-3.5 h-3.5 mr-2" />Edit</DropdownMenuItem>
                        {isAdmin && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={() => handleDelete(c.id)} className="text-red-500"><Trash2 className="w-3.5 h-3.5 mr-2" />Delete</DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        </div>
      </div>

      {/* View Client Dialog */}
      <Dialog open={!!viewClient} onOpenChange={() => setViewClient(null)}>
        <DialogContent className="bg-white border-gray-200 text-gray-900">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">Client Details</DialogTitle></DialogHeader>
          {viewClient && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><label className="text-xs text-gray-500">Name</label><p className="font-medium">{viewClient.name}</p></div>
                <div><label className="text-xs text-gray-500">Company</label><p>{viewClient.company || '--'}</p></div>
                <div><label className="text-xs text-gray-500">Email</label><p className="text-sm">{viewClient.email}</p></div>
                <div><label className="text-xs text-gray-500">Phone</label><p className="text-sm">{viewClient.phone || '--'}</p></div>
              </div>
              {viewClient.notes && (
                <div><label className="text-xs text-gray-500">Notes</label><p className="text-sm text-gray-600">{viewClient.notes}</p></div>
              )}
              <div className="flex gap-2">
                <Button variant="outline" className="border-gray-300" onClick={() => navigate(`/admin/clients/${viewClient.id}`)}>
                  <FolderOpen className="w-4 h-4 mr-1" /> View Portfolio
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit Client Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
          <DialogHeader><DialogTitle className="text-gray-900 uppercase">{editId ? 'Edit Client' : 'Add Client'}</DialogTitle></DialogHeader>
          <form onSubmit={handleSave} className="space-y-3">
            <Input placeholder="Full Name *" required value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Email *" type="email" required value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Phone" value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Company" value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="bg-gray-100 border-gray-200" />
            <Input placeholder="Address" value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="client-address" />
            <div className="grid grid-cols-3 gap-2">
              <Input placeholder="City" value={form.city} onChange={e => setForm(f => ({ ...f, city: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="client-city" />
              <Input placeholder="Province" value={form.province} onChange={e => setForm(f => ({ ...f, province: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="client-province" />
              <Input placeholder="Postal Code" value={form.postal_code} onChange={e => setForm(f => ({ ...f, postal_code: e.target.value }))} className="bg-gray-100 border-gray-200" data-testid="client-postal-code" />
            </div>
            {isAdmin && (
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Salesperson</label>
                <Select value={form.salesperson_id || '_none'} onValueChange={v => {
                  if (v === '_none') {
                    setForm(f => ({ ...f, salesperson: '', salesperson_id: '' }));
                  } else {
                    const s = staffList.find(st => st.id === v);
                    setForm(f => ({ ...f, salesperson: s?.name || '', salesperson_id: v }));
                  }
                }}>
                  <SelectTrigger className="bg-gray-100 border-gray-200" data-testid="salesperson-select"><SelectValue placeholder="Assign salesperson..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">-- None --</SelectItem>
                    {staffList.map(s => <SelectItem key={s.id} value={s.id}>{s.name} ({s.email})</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div>
              <label className="text-xs text-gray-500 mb-1 block">Preferred Makes</label>
              <div className="flex flex-wrap gap-1 mb-2">
                {form.preferred_makes.map(m => (
                  <Badge key={m} variant="outline" className="text-xs cursor-pointer hover:bg-red-50" onClick={() => setForm(f => ({ ...f, preferred_makes: f.preferred_makes.filter(x => x !== m) }))}>
                    {m} <X className="w-3 h-3 ml-1" />
                  </Badge>
                ))}
              </div>
              <div className="flex gap-2">
                <Input 
                  placeholder="Add make" 
                  value={makeInput} 
                  onChange={e => setMakeInput(e.target.value)} 
                  className="bg-gray-100 border-gray-200 flex-1"
                  list="makes-list"
                />
                <datalist id="makes-list">
                  {COMMON_MAKES.filter(m => !form.preferred_makes.includes(m)).map(m => <option key={m} value={m} />)}
                </datalist>
                <Button type="button" variant="outline" size="sm" onClick={() => {
                  if (makeInput.trim() && !form.preferred_makes.includes(makeInput.trim())) {
                    setForm(f => ({ ...f, preferred_makes: [...f.preferred_makes, makeInput.trim()] }));
                    setMakeInput('');
                  }
                }}>Add</Button>
              </div>
            </div>

            <Textarea placeholder="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} className="bg-gray-100 border-gray-200" rows={3} />

            {duplicateClient && !editId && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="duplicate-client-warning">
                <p className="text-sm font-medium text-amber-800 mb-1">Client already exists</p>
                <p className="text-xs text-amber-600 mb-2">
                  {duplicateClient.name} {duplicateClient.company ? `(${duplicateClient.company})` : ''} — {duplicateClient.email}
                </p>
                <button
                  type="button"
                  className="text-xs font-medium text-[#E31937] hover:underline"
                  onClick={() => { setDialogOpen(false); setDuplicateClient(null); navigate(`/admin/clients/${duplicateClient.id}`); }}
                  data-testid="open-existing-client"
                >
                  Click to open client info →
                </button>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" className="border-gray-300" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="bg-[#E31937] text-white" disabled={saving}>{saving ? 'Saving...' : editId ? 'Update' : 'Add Client'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <BulkActionBar count={selected.length} onDelete={async () => {
        if (!window.confirm(`Delete ${selected.length} client(s)?`)) return;
        try { await clientsAPI.bulkDelete(selected); toast.success(`${selected.length} client(s) deleted`); setSelected([]); loadClients(); } catch { toast.error('Failed'); }
      }} onClear={() => setSelected([])} label="client(s)" />
    </div>
  );
}
